/* ============================================================================
   clp-base.js — behaviour for Seven Minerals campaign landing pages
   ----------------------------------------------------------------------------
   Shared by every clp-* section. Previously inline in each page template.

   EVERYTHING IS EVENT-DELEGATED. The old version wired 134 inline onclick
   attributes, which meant the entire page stopped working under any Content
   Security Policy without 'unsafe-inline'. Nothing here needs an inline
   handler: sections mark elements with data-clp-* attributes and one listener
   on document handles them, including elements added to the DOM later.

   Removed from the original (verified as called 0 times from the markup of all
   three CLP templates, the Summer End Sale page and engine v2):
     clvVar, clvSubVar, clvCartAdd, clvAdd, clvAddSub
   clvVar mattered: it contained `now.textContent = '$'+(p*0.75)` under a
   comment reading "25% off one-time". Reviving it during a 20% sale would have
   silently rendered every price at 25% off. Deleted rather than left lying
   around.

   clvFilter was defined twice; the surviving copy set inline
   style.display='none'!important, which contradicts the LAYOUT GUARD in
   clp-base.css ("filtering happens by class, never inline style.display").
   Filtering here toggles the namespaced .clvp-hide class, as the guard intends.
   ============================================================================ */
(function () {
  'use strict';

  // Idempotence guard. Every clp-* section includes the asset tags via the
  // clp-assets snippet so sections can be dropped onto a template in any order
  // and any combination. Without this, a page with five clp-* sections would
  // bind the delegated click listener five times and a single Add to Cart tap
  // would add five items.
  if (window.__clpBaseLoaded) return;
  window.__clpBaseLoaded = true;

  var PAGE = '.clvp-page';

  /* ── helpers ─────────────────────────────────────────────────────────────── */
  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }
  function money(n) {
    return '$' + n.toFixed(2);
  }

  /* ── scroll reveal + sticky bar ──────────────────────────────────────────── */
  function initReveals(root) {
    var targets = (root || document).querySelectorAll(PAGE + ' [data-anim], ' + PAGE + ' [data-stagger]');
    if (!targets.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        // .clvp-inview, NOT a bare `visible` class. See LAYOUT GUARD in clp-base.css.
        if (e.isIntersecting) e.target.classList.add('clvp-inview');
      });
    }, { threshold: 0.12 });
    targets.forEach(function (el) { io.observe(el); });
  }

  function initSticky() {
    var sticky = document.querySelector('[data-clp-sticky]');
    var hero = document.querySelector('.clvx-hero');
    if (!sticky || !hero || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      sticky.classList.toggle('clvp-inview', !entries[0].isIntersecting);
    }, { threshold: 0 }).observe(hero);
  }

  /* ── countdown ───────────────────────────────────────────────────────────────
     data-deadline is ISO 8601 with an explicit offset. HOUSE RULE: Seven
     Minerals sales end 11:59pm Eastern and the copy must say "ET". The offset is
     -04:00 during EDT (mid-Mar to early Nov) and -05:00 during EST (early Nov to
     mid-Mar) -- never copy one campaign's offset into a campaign in the other
     half of the year.

     On expiry the old version swapped only the timer's own markup, leaving every
     "20% off" claim and all 45 discount-code mentions on the page. Here expiry
     also stamps .clp-offer-ended on the page root, which hides anything marked
     [data-clp-offer], so the offer messaging goes with the timer.
     -------------------------------------------------------------------------- */
  function initCountdown() {
    var cd = document.querySelector('.clvx-countdown');
    if (!cd) return;
    var raw = cd.getAttribute('data-deadline');
    var end = raw ? new Date(raw).getTime() : NaN;
    if (isNaN(end)) { cd.hidden = true; return; }

    // Announce politely: the timer updates every second, so only the coarse
    // state change is worth reading out, not each tick.
    cd.setAttribute('aria-live', 'polite');
    cd.setAttribute('aria-atomic', 'true');

    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    var slot = {
      d: cd.querySelector('[data-d]'), h: cd.querySelector('[data-h]'),
      m: cd.querySelector('[data-m]'), s: cd.querySelector('[data-s]')
    };
    var timer = null;

    function expire() {
      if (timer) clearTimeout(timer);
      cd.innerHTML = '<span class="clvx-cd-ended">This event has ended</span>';
      var page = closest(cd, PAGE) || document.body;
      page.classList.add('clp-offer-ended');
    }

    function tick() {
      var diff = end - Date.now();
      if (diff <= 0) return expire();
      var d = Math.floor(diff / 864e5),
          h = Math.floor(diff % 864e5 / 36e5),
          m = Math.floor(diff % 36e5 / 6e4),
          s = Math.floor(diff % 6e4 / 1e3);
      if (slot.d) slot.d.textContent = pad(d);
      if (slot.h) slot.h.textContent = pad(h);
      if (slot.m) slot.m.textContent = pad(m);
      if (slot.s) slot.s.textContent = pad(s);
      timer = setTimeout(tick, 1000);
    }
    tick();
  }

  /* ── copy discount code ──────────────────────────────────────────────────── */
  function copyCode(el) {
    var code = el.getAttribute('data-clp-copy');
    if (!code) return;
    var done = function () {
      var slot = el.querySelector('.a') || el;
      var original = slot.textContent;
      slot.textContent = 'Copied!';
      setTimeout(function () { slot.textContent = original; }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(done);
    } else {
      done();
    }
  }

  /* ── grid filter (real ARIA tab pattern) ──────────────────────────────────────
     The old markup declared role="tablist" but contained 0 role="tab", 0
     aria-selected and 0 aria-controls, so a screen reader announced a tablist
     with no tabs -- worse than no ARIA at all. Sections now emit real tabs and
     this keeps aria-selected in sync and supports arrow-key roving focus.
     -------------------------------------------------------------------------- */
  function applyFilter(btn) {
    var cat = btn.getAttribute('data-clp-filter');
    var tablist = closest(btn, '[role="tablist"]') || btn.parentElement;
    var gridId = btn.getAttribute('aria-controls');
    var grid = gridId ? document.getElementById(gridId) : document.querySelector('[data-clp-grid]');
    if (!grid) return;

    if (tablist) {
      tablist.querySelectorAll('[data-clp-filter]').forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.setAttribute('tabindex', on ? '0' : '-1');
      });
    }

    grid.querySelectorAll('[data-cats]').forEach(function (card) {
      var cats = (card.getAttribute('data-cats') || '').split('|');
      var show = cat === 'All' || cats.indexOf(cat) >= 0;
      // class, never inline style.display -- see LAYOUT GUARD in clp-base.css
      card.classList.toggle('clvp-hide', !show);
    });
  }

  function onTablistKey(e) {
    var btn = closest(e.target, '[data-clp-filter]');
    if (!btn) return;
    var keys = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 };
    if (!(e.key in keys)) return;
    var tablist = closest(btn, '[role="tablist"]');
    if (!tablist) return;
    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[data-clp-filter]'));
    var next = tabs[(tabs.indexOf(btn) + keys[e.key] + tabs.length) % tabs.length];
    if (next) { e.preventDefault(); next.focus(); applyFilter(next); }
  }

  /* ── FAQ accordion ───────────────────────────────────────────────────────────
     The old version was onclick="this.parentElement.classList.toggle('open')"
     with the answer hidden only by max-height:0;overflow:hidden. The text stayed
     in the accessibility tree, so a screen reader read out every answer while it
     looked closed, and the open/closed state was never announced. Now the
     trigger carries aria-expanded and the panel is genuinely [hidden].
     -------------------------------------------------------------------------- */
  function toggleFaq(btn) {
    var item = closest(btn, '.clvp-faq-item');
    if (!item) return;
    var panel = item.querySelector('.clvp-faq-a');
    var open = item.classList.toggle('open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) {
      if (open) {
        panel.hidden = false;
      } else {
        // let the max-height transition run before removing from the a11y tree
        setTimeout(function () { if (!item.classList.contains('open')) panel.hidden = true; }, 400);
      }
    }
  }

  /* ── add to cart ─────────────────────────────────────────────────────────── */
  function cartAdd(variantId, btn) {
    if (!variantId) return;
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || 'add failed'); });
        return res.json();
      })
      .then(function () {
        btn.textContent = '✓ Added';
        btn.classList.add('added');
        // Theme/app globals. Guarded because they are not guaranteed to exist.
        try { if (typeof window.monster_refresh === 'function') window.monster_refresh(); } catch (e) {}
        try { if (typeof window.updateCartCount === 'function') window.updateCartCount(); } catch (e) {}
        var drawer = document.querySelector('cart-drawer-component, cart-drawer');
        try { if (drawer && typeof drawer.open === 'function') drawer.open(); } catch (e) {}
        document.querySelectorAll('.cart-count-bubble span, #cart-count, .cart-count').forEach(function (el) {
          var n = parseInt(el.textContent || '0', 10);
          if (!isNaN(n)) el.textContent = n + 1;
        });
        setTimeout(function () {
          btn.textContent = original;
          btn.disabled = false;
          btn.classList.remove('added');
        }, 2000);
      })
      .catch(function () {
        btn.textContent = original;
        btn.disabled = false;
        window.location.href = '/cart';
      });
  }

  /* ── pack selector ───────────────────────────────────────────────────────────
     Pack size comes from the variant title ("Pack of 3"), parsed server-side into
     data-qty. Do NOT assume the first option is a 1-pack: castor-oil-for-naval-
     oiling-detox ships as Pack of 2 / 3 / 6 with no single. data-base is the
     per-unit price of the SMALLEST available pack, so "Save %" stays honest.
     -------------------------------------------------------------------------- */
  function selectPack(btn) {
    var box = closest(btn, '[data-buy]');
    if (!box || btn.disabled) return;
    box.querySelectorAll('[data-clp-pack]').forEach(function (b) {
      b.classList.remove('active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('active');
    btn.setAttribute('aria-pressed', 'true');

    var price = parseFloat(btn.getAttribute('data-price'));
    var qty = parseInt(btn.getAttribute('data-qty'), 10) || 1;
    var base = parseFloat(box.getAttribute('data-base')) || (price / qty);
    var unit = price / qty;

    var now = box.querySelector('.clvx-price .now');
    var per = box.querySelector('.clvsw-per');
    var save = box.querySelector('.clvsw-save');
    if (now) now.textContent = money(price);
    if (per) per.textContent = qty > 1 ? money(unit) + '/ea' : '';
    if (save) {
      var pct = Math.round((1 - unit / base) * 100);
      save.textContent = pct > 0 ? 'Save ' + pct + '%' : '';
    }
  }

  function addSelected(btn) {
    var box = closest(btn, '[data-buy]');
    var chosen = box && (box.querySelector('[data-clp-pack].active') || box.querySelector('[data-clp-pack]:not(:disabled)'));
    var vid = chosen ? Number(chosen.getAttribute('data-vid')) : Number(btn.getAttribute('data-variant'));
    cartAdd(vid, btn);
  }

  /* ── one delegated listener ──────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var el;
    if ((el = closest(e.target, '[data-clp-copy]'))) { e.preventDefault(); return copyCode(el); }
    if ((el = closest(e.target, '[data-clp-filter]'))) { e.preventDefault(); return applyFilter(el); }
    if ((el = closest(e.target, '[data-clp-pack]'))) { e.preventDefault(); return selectPack(el); }
    if ((el = closest(e.target, '[data-clp-add]'))) { e.preventDefault(); return addSelected(el); }
    if ((el = closest(e.target, '[data-clp-faq]'))) { e.preventDefault(); return toggleFaq(el); }
  });
  document.addEventListener('keydown', onTablistKey);

  /* ── boot ────────────────────────────────────────────────────────────────── */
  function boot() {
    initReveals(document);
    initSticky();
    initCountdown();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor: re-init when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    initSticky();
    initCountdown();
  });
})();
